import { formatAge } from '../../lib/format.ts'
import type { FleetPayload } from '../../lib/sse/types.ts'
import { RelativeTime } from './RelativeTime.tsx'
import { StatusDot } from './StatusDot.tsx'

/**
 * Freshness stamp of the panels fed by reference data.
 *
 * O stream carrega duas naturezas de dado, e elas falham de formas diferentes:
 * as **métricas** vêm de contadores em memória e continuam exatas mesmo com o
 * banco fora do ar; a **referência** — nomes de tenant, quotas, versões, frota —
 * vem do PostgreSQL com cache de 10 s. Quando o banco cai, o servidor serve o
 * último valor bom e levanta `reference_stale`.
 *
 * Daí a regra que este componente existe para aplicar: **marcar, nunca
 * esconder**. Sumir com o painel de frota levaria o operador a achar que a
 * plataforma parou, quando os números de ingestão ao lado estão corretos. O que
 * ele precisa é saber que aquela seção específica congelou, e há quanto tempo.
 */
export function ReferenceStamp({
  fleet,
  lastFleetAt,
}: {
  fleet: FleetPayload | null
  /** Browser clock of the last `fleet` event — a freshness the payload cannot state. */
  lastFleetAt: number | null
}) {
  if (fleet !== null && fleet.reference_stale) {
    // -1 é o contrato para "nenhum ciclo bem-sucedido desde o start": o banco
    // está fora desde o começo e não há nem um valor velho para datar.
    const never = fleet.reference_age_seconds < 0

    return (
      <span
        role="status"
        className="flex items-center gap-1.5 text-warn"
      >
        <StatusDot tone="warn" />
        <span className="uppercase tracking-label">
          {never ? 'referência indisponível' : 'referência defasada'}
        </span>
        {!never && (
          <span className="tnum text-fg-muted">
            {formatAge(fleet.reference_age_seconds * 1000)}
          </span>
        )}
      </span>
    )
  }

  return (
    <span>
      frota <RelativeTime since={lastFleetAt} intervalMs={2_000} />
    </span>
  )
}
